import { useQueryClient } from '@tanstack/react-query'
import { type Note, type Change, NoteType, isPrivateNote } from '../utils/note'
import { cacheKeyFromNoteType, noteQueryKey } from '../utils/queryKeyFactory'
import { useAuthenticatedUser } from '../providers/UserProvider'
import { AuthType } from '../utils/User'
import {
  cachedNotesContext,
  getCachedData,
  getNoteFromCache,
  updateCache,
} from '../contexts/CachedNotes'
import * as supabase from '../lib/supabase/noteOperations'
import * as cloudkit from '../lib/cloudkit/noteOperations'
import { trackEvent } from '../lib/analytics'
import { useSafeMutation } from './useSafeMutation'

export function useSaveNote(successCallback?: (data: Note) => void) {
  const user = useAuthenticatedUser()
  const queryClient = useQueryClient()
  const cachedNotesQueryClient = useQueryClient({
    context: cachedNotesContext,
  })

  // TODO should always return a note. if undefined, throw an error
  return useSafeMutation<Note, Error, Change>({
    mutationFn: (change: Change) => {
      const queryKey = noteQueryKey(change)
      // always get the latest version of the RecordChangeTag
      let note = queryClient.getQueryData<Note>(queryKey)

      // If not found in main cache, try cached notes context (for filter & search view)
      if (!note && change.recordName) {
        const cacheKey = cacheKeyFromNoteType(change.noteType, user)
        note = getNoteFromCache(
          cachedNotesQueryClient,
          cacheKey,
          change.recordName
        )

        // If found in cached context, add it to main cache for future use
        if (note) {
          queryClient.setQueryData(queryKey, note)
        }
      }

      // Used by starter note creator, because we don't create the note by opening the calendar date
      if (change.forceCreate) {
        note = {
          recordName: change.recordName,
          noteType: change.noteType,
          parent: change.parent, // Assign ID of parent in case it's supabase, ignored by CloudKit
          content: change.content,
          filename: change.filename,
        }
      }

      if (!note) {
        console.error(
          '[useSaveNote] Note not found in both main and cached contexts',
          change
        )
        trackEvent('web.useSaveNote.error', {
          message: 'Note not found in both contexts',
          change,
        })
        throw new Error('Note not found')
      }

      // console.debug('[useSaveNote] saving', note, change, user)

      switch (user.authType) {
        case AuthType.CLOUDKIT: {
          return cloudkit.saveNote(
            note,
            change.content,
            change.attachments,
            change.modificationDate
          )
        }
        case AuthType.SUPABASE: {
          return supabase.saveNote(
            user.teamUserId,
            note,
            change.content,
            change.attachments
          )
        }
        case AuthType.CLOUDKIT_SUPABASE: {
          return isPrivateNote(change.noteType)
            ? cloudkit.saveNote(
                note,
                change.content,
                change.attachments,
                change.modificationDate
              )
            : supabase.saveNote(
                user.teamUserId,
                note,
                change.content,
                change.attachments
              )
        }
      }
    },

    onMutate: (change) => {
      if (!change.recordName) return

      const queryKey = noteQueryKey(change)
      const note = queryClient.getQueryData<Note>(queryKey)

      // Optimistically update the note in cache
      if (note) {
        const updatedNote = {
          ...note,
          content: change.content,
          fileModifiedAt: change.modificationDate,
        }
        queryClient.setQueryData(noteQueryKey(change), updatedNote)
      }

      const cacheKey = cacheKeyFromNoteType(change.noteType, user)
      const previousNotes = getCachedData(cachedNotesQueryClient, cacheKey)

      // Optimistically update the note in the general cache, that's where we will fetch it when opening the note.
      const previousNote = previousNotes.get(change.recordName)
      if (previousNote) {
        const updatedNote = {
          ...previousNote,
          content: change.content,
          fileModifiedAt: change.modificationDate, // Important, because in TipTapEditor we check fileModifiedAt for updates
        }
        updateCache(cachedNotesQueryClient, [updatedNote], cacheKey)
      }
    },

    onSuccess: (note, change) => {
      trackEvent('WEB - Note Updated', {
        recordName: change.recordName,
        noteType: NoteType[change.noteType],
        parent: change.parent,
        filename: change.filename,
      })

      queryClient.setQueryData(noteQueryKey(change), note)
      const cacheKey = cacheKeyFromNoteType(note.noteType, user)
      updateCache(cachedNotesQueryClient, [note], cacheKey)

      successCallback?.(note)
    },
    onError: (error, change) => {
      trackEvent('WEB - Note Updated Failed', {
        error,
      })
      if (error.message.includes('oplock error')) {
        trackEvent('WEB - Note Updated Failed - Oplock Error', {
          error,
        })

        // eslint-disable-next-line no-console
        console.log('[useSaveNote] Conflict detected, refetch note.')

        // Refetch the note upon error, so the next time it works. This would be merged once we receive it in TipTapEditor
        void queryClient.invalidateQueries({
          queryKey: noteQueryKey(change),
          refetchType: 'all',
        })
      }
    },
    retry: 3, // Retry a few times, in case we resolve the oplock error
  })
}
